import React, { useCallback, useEffect, useState, useRef } from 'react';
// Inline SVGs — avoid loading lucide-react in critical path
function PlayIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none" className="ml-1" data-id="element-58">
      <polygon points="6,3 20,12 6,21" data-id="element-59" />
    </svg>;
}
function PauseIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none" data-id="element-60">
      <rect x="6" y="4" width="4" height="16" data-id="element-61" />
      <rect x="14" y="4" width="4" height="16" data-id="element-62" />
    </svg>;
}
interface BackgroundAudioProps {
  hidden?: boolean;
}
export function BackgroundAudio({
  hidden = false
}: BackgroundAudioProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioStartedRef = useRef(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.2;
    audio.muted = true;
    // Try autoplay immediately
    const tryAutoplay = () => {
      if (audioStartedRef.current) return;
      audio.play().then(() => {
        // Autoplay worked (muted) — now unmute
        audio.muted = false;
        audioStartedRef.current = true;
        setIsPlaying(true);
        removeInteractionListeners();
      }).catch(() => {
        // Autoplay blocked — will start on first interaction
      });
    };
    // On ANY user interaction, start audio once then clean up
    const handleFirstInteraction = () => {
      if (audioStartedRef.current) return;
      audioStartedRef.current = true;
      audio.muted = false;
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        // Last resort: try muted first, then unmute
        audio.muted = true;
        audio.play().then(() => {
          audio.muted = false;
          setIsPlaying(true);
        }).catch(() => {});
      });
      removeInteractionListeners();
    };
    const interactionEvents = ['click', 'touchstart', 'touchend', 'mousedown', 'scroll', 'keydown', 'pointerdown', 'wheel'];
    const addInteractionListeners = () => {
      interactionEvents.forEach(evt => {
        document.addEventListener(evt, handleFirstInteraction, {
          once: true,
          passive: true
        });
      });
    };
    const removeInteractionListeners = () => {
      interactionEvents.forEach(evt => {
        document.removeEventListener(evt, handleFirstInteraction);
      });
    };
    // Attempt autoplay after 2 seconds
    const initialTimer = setTimeout(tryAutoplay, 2000);
    // Retry once after 2400ms in case DOM needs to settle
    const retryTimer = setTimeout(tryAutoplay, 2400);
    // If autoplay didn't work, set up interaction listeners as fallback
    const fallbackTimer = setTimeout(() => {
      if (!audioStartedRef.current) {
        addInteractionListeners();
      }
    }, 2500);
    // Sync UI state with actual audio events (for normal control after startup)
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(retryTimer);
      clearTimeout(fallbackTimer);
      removeInteractionListeners();
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Ensure unmuted on manual control
    if (audio.muted) {
      audio.muted = false;
    }
    audioStartedRef.current = true;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(error => {
        console.error('Audio playback failed:', error);
      });
    }
  }, [isPlaying]);
  return <>
      <audio ref={audioRef} src="https://cdn.shopify.com/s/files/1/0981/0781/8286/files/Audio_pagina_888_Completo.mp3?v=1777084333" preload="none" loop playsInline data-id="element-63" />
      {!hidden && <button onClick={togglePlay} className={`fixed top-16 right-4 z-[100] w-11 h-11 rounded-full bg-[#0A0A0B]/80 backdrop-blur-sm border border-gold/30 flex items-center justify-center text-gold shadow-[0_0_15px_rgba(212,175,55,0.2)] transition-all hover:scale-110 hover:border-gold hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] ${!isPlaying ? 'animate-pulse' : ''}`} aria-label={isPlaying ? 'Pausar música de fondo' : 'Reproducir música de fondo'} data-id="element-64">
          {isPlaying ? <PauseIcon data-id="element-65" /> : <PlayIcon data-id="element-66" />}
        </button>}
    </>;
}